/**
 * Validation utilities for configuration forms
 */

export const validateConfigField = (section, fieldName, value, schema) => {
  const fieldConfig = schema[section]?.fields?.[fieldName];
  if (!fieldConfig) return null;

  // Skip validation for undefined/null values that have defaults
  if (value === undefined || value === null) {
    return null;
  }

  const errors = [];

  // Type validation
  switch (fieldConfig.type) {
    case 'number':
      if (isNaN(value)) {
        errors.push(`${fieldConfig.label} must be a valid number`);
      } else {
        if (fieldConfig.min !== undefined && value < fieldConfig.min) {
          errors.push(`${fieldConfig.label} must be at least ${fieldConfig.min}`);
        }
        if (fieldConfig.max !== undefined && value > fieldConfig.max) {
          errors.push(`${fieldConfig.label} must be at most ${fieldConfig.max}`);
        }
      }
      break;

    case 'select':
      if (fieldConfig.options && !fieldConfig.options.includes(value)) {
        errors.push(`${fieldConfig.label} must be one of: ${fieldConfig.options.join(', ')}`);
      }
      break;

    case 'boolean':
      if (typeof value !== 'boolean') {
        errors.push(`${fieldConfig.label} must be true or false`);
      }
      break;

    default:
      if (typeof value !== 'string') {
        errors.push(`${fieldConfig.label} must be a text value`);
      }
  }

  return errors.length > 0 ? errors : null;
};

export const validateWeightCombinations = (section, config) => {
  const errors = [];

  if (section === 'search') {
    const originalWeight = config.original_score_weight || 0;
    const hydeWeight = config.hyde_boost_weight || 0;
    const total = originalWeight + hydeWeight;
    
    if (Math.abs(total - 1.0) > 0.01) {
      errors.push('Original Score Weight + Enhancement Weight must equal 1.0');
    }
  }

  if (section === 'scg') {
    const keywordWeight = config.keyword_match_weight || 0;
    const semanticWeight = config.semantic_match_weight || 0;
    const total = keywordWeight + semanticWeight;
    
    if (Math.abs(total - 1.0) > 0.01) {
      errors.push('Keyword Match Weight + Semantic Match Weight must equal 1.0');
    }
  }

  return errors;
};

export const validateSectionConfig = (section, config, schema) => {
  const errors = [];
  const sectionSchema = schema[section];
  
  if (!sectionSchema) {
    errors.push(`Unknown configuration section: ${section}`);
    return errors;
  }

  // Validate individual fields, but skip provider-specific fields that aren't relevant
  Object.entries(config).forEach(([fieldName, value]) => {
    // Skip validation for model fields that don't match the current provider
    if (section === 'models') {
      const currentProvider = config.llm_provider || 'bedrock';
      
      // Skip Bedrock model validation when using Anthropic
      if (fieldName === 'bedrock_model_id' && currentProvider !== 'bedrock') {
        return;
      }
      
      // Skip Anthropic model validation when using Bedrock
      if (fieldName === 'anthropic_model_id' && currentProvider !== 'anthropic') {
        return;
      }
    }
    
    const fieldErrors = validateConfigField(section, fieldName, value, schema);
    if (fieldErrors) {
      errors.push(...fieldErrors);
    }
  });

  // Validate weight combinations
  const weightErrors = validateWeightCombinations(section, config);
  errors.push(...weightErrors);

  // Custom validations
  if (section === 'models') {
    if (config.temperature !== undefined && (config.temperature < 0 || config.temperature > 1)) {
      errors.push('Temperature must be between 0.0 and 1.0');
    }
    
    if (config.max_tokens !== undefined && config.max_tokens < 100) {
      errors.push('Max tokens should be at least 100 for meaningful responses');
    }
  }

  if (section === 'processing') {
    if (config.chunk_overlap !== undefined && config.chunk_size !== undefined) {
      if (config.chunk_overlap >= config.chunk_size) {
        errors.push('Chunk overlap must be less than chunk size');
      }
    }
  }

  if (section === 'hyde') {
    if (config.original_weight !== undefined) {
      const hypotheticalWeight = 1.0 - config.original_weight;
      if (hypotheticalWeight < 0.1) {
        errors.push('Original weight is too high - enhanced documents need at least 10% weight');
      }
    }
  }

  return errors;
};

export const getFieldWarnings = (section, fieldName, value) => {
  const warnings = [];

  // Performance warnings
  if (section === 'models') {
    if (fieldName === 'max_tokens' && value > 8000) {
      warnings.push('Very high token limits may increase response time and costs');
    } else if (fieldName === 'max_tokens' && value > 4000) {
      warnings.push('High token limits may increase response time and costs');
    }
    
    if (fieldName === 'batch_size' && value > 64) {
      warnings.push('Large batch sizes may require more memory');
    }
  }

  if (section === 'search') {
    if (fieldName === 'top_k' && value > 25) {
      warnings.push('High result counts may impact search performance');
    }
  }

  if (section === 'hyde') {
    if (fieldName === 'num_hypotheticals' && value > 3) {
      warnings.push('More enhanced documents increase processing time');
    }
    
    if (fieldName === 'hyde_temperature' && value < 0.3) {
      warnings.push('Low temperature may reduce diversity of enhanced documents');
    }
  }

  if (section === 'processing') {
    if (fieldName === 'ocr_dpi' && value > 400) {
      warnings.push('High DPI settings significantly increase OCR processing time');
    }
    
    if (fieldName === 'processing_timeout' && value < 60) {
      warnings.push('Short timeouts may cause failures for large documents');
    }
  }

  // Model-specific warnings
  if (section === 'models' && fieldName === 'embedding_model') {
    const modelInfo = {
      'sentence-transformers/all-mpnet-base-v2': { dims: 768, speed: 'medium', quality: 'high' },
      'sentence-transformers/all-MiniLM-L12-v2': { dims: 384, speed: 'fast', quality: 'medium' },
      'sentence-transformers/all-roberta-large-v1': { dims: 1024, speed: 'slow', quality: 'highest' },
      'BAAI/bge-large-en-v1.5': { dims: 1024, speed: 'slow', quality: 'highest' }
    };
    
    const info = modelInfo[value];
    if (info) {
      if (info.speed === 'slow') {
        warnings.push(`${value} provides ${info.quality} quality but slower performance`);
      }
      warnings.push(`This model produces ${info.dims}-dimensional embeddings`);
    }
  }

  return warnings;
};

export const formatConfigForDisplay = (config) => {
  const formatted = { ...config };
  
  // Round numeric values for display
  Object.keys(formatted).forEach(key => {
    if (typeof formatted[key] === 'number') {
      if (formatted[key] % 1 !== 0) { // If decimal
        formatted[key] = Math.round(formatted[key] * 1000) / 1000; // Round to 3 decimal places
      }
    }
  });
  
  return formatted;
};

export const hasUnsavedChanges = (original, current) => {
  return JSON.stringify(original) !== JSON.stringify(current);
};

export const getChangedFields = (original, current) => {
  const changes = {};
  
  Object.keys(current).forEach(key => {
    if (original[key] !== current[key]) {
      changes[key] = {
        from: original[key],
        to: current[key]
      };
    }
  });
  
  return changes;
};